import React from 'react';
import { Row, Col, Input, Modal, ModalHeader, ModalBody } from 'reactstrap';
import {
  FOB_INCOTERM_ID,
  INCOTERM_PORT_OPTIONS,
  MULTIPORT_OPTIONS,
  TIMELINE_OPTIONS,
} from '../shared/constants';
import { formatParcelSize, needsLocationQuantity } from '../shared/utils';
import {
  CounterProductAlert,
  CounterModalLoading,
  CounterModalFooter,
  CounterFieldError,
  CounterDeliveryBasisFields,
  useLocationBlockRenderer,
} from '../shared/counterOfferShared';

const CounterOfferModalNonLpg = ({
  isOpen,
  toggle,
  formData,
  onFormChange,
  onSave,
  loading,
  saveDisabled = false,
  errors = {},
  form,
  incotermTypeOptions,
  incotermRouteOptions: _incotermRouteOptions,
  incotermLocationOptions,
  fobRouteOptions,
}) => {
  const renderLocationBlock = useLocationBlockRenderer({
    incotermLocationOptions,
    onFormChange,
    errors,
  });

  const isFobIncoterm = formData.incotermId === FOB_INCOTERM_ID;

  const showMultiPortOption =
    !isFobIncoterm && formData.incotermPortType === 'MultiPort';

  const showLocation2 =
    !isFobIncoterm &&
    formData.incotermPortType === 'MultiPort' &&
    (formData.incotermMultiPortType === '2Port' ||
      formData.incotermMultiPortType === '3Port');

  const showLocation3 =
    !isFobIncoterm &&
    formData.incotermPortType === 'MultiPort' &&
    formData.incotermMultiPortType === '3Port';

  const showLocationQuantity = needsLocationQuantity(formData);

  const timelineLabel =
    TIMELINE_OPTIONS.find(option => option.value === formData.timelineType)
      ?.label || 'Accepted Discharged Dates';

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      backdrop='static'
      size='xl'
      scrollable
    >
      <ModalHeader toggle={toggle}>Add Counter Requirement</ModalHeader>
      <ModalBody className='p-4'>
        <CounterProductAlert form={form} />

        {loading ? (
          <CounterModalLoading />
        ) : (
          <>
            <CounterDeliveryBasisFields
              formData={formData}
              onFormChange={onFormChange}
            />

            <Row className='g-3 mb-4'>
              <Col md={4}>
                <div className='form-group'>
                  <label className='fw-semibold text-muted fs-13 mb-1'>
                    Type
                  </label>
                  <Input
                    type='select'
                    value={formData.incotermId}
                    onChange={e => onFormChange('incotermId', e.target.value)}
                    className='py-2 text-dark fw-medium form-select'
                    invalid={!!errors.incotermId}
                  >
                    <option value=''>Please select</option>
                    {incotermTypeOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                  <CounterFieldError message={errors.incotermId} />
                </div>
              </Col>

              {formData.incotermId !== FOB_INCOTERM_ID && (
                <Col md={4}>
                  <div className='form-group'>
                    <label className='fw-semibold text-muted fs-13 mb-1'>
                      Port(s)
                    </label>
                    <Input
                      type='select'
                      value={formData.incotermPortType}
                      onChange={e =>
                        onFormChange('incotermPortType', e.target.value)
                      }
                      className='py-2 text-dark fw-medium form-select'
                      invalid={!!errors.incotermPortType}
                    >
                      <option value=''>Please select</option>
                      {INCOTERM_PORT_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Input>
                    <CounterFieldError message={errors.incotermPortType} />
                  </div>
                </Col>
              )}

              {formData.incotermId === FOB_INCOTERM_ID && (
                <Col md={4}>
                  <div className='form-group'>
                    <label className='fw-semibold text-muted fs-13 mb-1'>
                      Route
                    </label>
                    <Input
                      type='select'
                      value={formData.incotermRouteId}
                      onChange={e =>
                        onFormChange('incotermRouteId', e.target.value)
                      }
                      className='py-2 text-dark fw-medium form-select'
                      invalid={!!errors.incotermRouteId}
                    >
                      <option value=''>Please select</option>
                      {fobRouteOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Input>
                    <CounterFieldError message={errors.incotermRouteId} />
                  </div>
                </Col>
              )}

              {showMultiPortOption && (
                <Col md={4}>
                  <div className='form-group'>
                    <label className='fw-semibold text-muted fs-13 mb-1'>
                      Multiport Option
                    </label>
                    <Input
                      type='select'
                      value={formData.incotermMultiPortType}
                      onChange={e =>
                        onFormChange('incotermMultiPortType', e.target.value)
                      }
                      className='py-2 text-dark fw-medium form-select'
                      invalid={!!errors.incotermMultiPortType}
                    >
                      <option value=''>Please select</option>
                      {MULTIPORT_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Input>
                    <CounterFieldError message={errors.incotermMultiPortType} />
                  </div>
                </Col>
              )}
            </Row>

            <div className='mb-3'>
              <span className='badge bg-primary-subtle text-primary p-2 px-3 fs-13 fw-medium rounded-2'>
                Total Quantity:{' '}
                <span className='fw-bold ms-1'>
                  {formatParcelSize(formData.deliveryBasis)}
                </span>
              </span>
            </div>

            <Row className='g-3 mb-4'>
              <Col md={isFobIncoterm ? 6 : 4}>
                {renderLocationBlock({
                  label:
                    showLocation2 || showLocation3 ? 'Location 1' : 'Location',
                  locationValue: formData.incotermLocationId,
                  locationField: 'incotermLocationId',
                  multiSelectField: 'incotermFOB',
                  dateFromValue: formData.startDate,
                  dateFromField: 'startDate',
                  dateToValue: formData.endDate,
                  dateToField: 'endDate',
                  quantityValue: formData.quantityLocation1,
                  quantityField: 'quantityLocation1',
                  showQuantity: showLocationQuantity,
                  isMultiSelect: isFobIncoterm,
                  multiSelectValue: formData.incotermFOB,
                })}
              </Col>

              {showLocation2 && (
                <Col md={4}>
                  {renderLocationBlock({
                    label: 'Location 2',
                    locationValue: formData.incotermLocation2Id,
                    locationField: 'incotermLocation2Id',
                    dateFromValue: formData.startDate2,
                    dateFromField: 'startDate2',
                    dateToValue: formData.endDate2,
                    dateToField: 'endDate2',
                    quantityValue: formData.quantityLocation2,
                    quantityField: 'quantityLocation2',
                    showQuantity: showLocationQuantity,
                  })}
                </Col>
              )}

              {showLocation3 && (
                <Col md={4}>
                  {renderLocationBlock({
                    label: 'Location 3',
                    locationValue: formData.incotermLocation3Id,
                    locationField: 'incotermLocation3Id',
                    dateFromValue: formData.startDate3,
                    dateFromField: 'startDate3',
                    dateToValue: formData.endDate3,
                    dateToField: 'endDate3',
                    quantityValue: formData.quantityLocation3,
                    quantityField: 'quantityLocation3',
                    showQuantity: showLocationQuantity,
                  })}
                </Col>
              )}
            </Row>

            <div className='p-3 bg-light rounded-3 border-0 fs-13 fw-medium text-dark mb-1'>
              <span className='text-muted me-2'>Timeline:</span> {timelineLabel}
            </div>
          </>
        )}
      </ModalBody>

      <CounterModalFooter
        loading={loading}
        saveDisabled={saveDisabled}
        onCancel={toggle}
        onSave={onSave}
        formData={formData}
      />
    </Modal>
  );
};

export default CounterOfferModalNonLpg;
